/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import {
	Router,
	ActivationEnd,
	Event,
	ActivatedRouteSnapshot,
	ActivatedRoute,
	NavigationEnd,
	Params,
	convertToParamMap,
	ParamMap,
	ActivationStart,
	NavigationStart,
} from '@angular/router';
import { BehaviorSubject, of, EMPTY, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { LoginComponent } from '../common/auth/components/login/login.component';

//TODO - come up with some method of expressing all routes as enums or something
// thinking we tackle once there's more than one route like this?
// export const fullyAngularRoutes = { projects: 'projects' };
// ...nope, can't use this yet, the route isn't fully angular because of the reports panel
// flipping this on resulted in TP-1563 because there's nothing generating the dynamic classes
export const fullyAngularRoutes: { [key: string]: string } = {};

/**
 * Basically has the same structure as an ActivatedRouteSnapshot, but only paramater related data
 */
export interface IAllParams {
	params: Params;
	queryParams: Params;
	paramMap: ParamMap;
	queryParamMap: ParamMap;
}

function makeAllParams(params: Params = {}, queryParams: Params = {}): IAllParams {
	return {
		params,
		queryParams,
		paramMap: convertToParamMap(params),
		queryParamMap: convertToParamMap(queryParams),
	};
}

/**
 * Take an ActivatedRouteSnapshot, recurse through the whole thing from the snapshot to the bottom (it does not look up). And combine all the
 * route parameters into a single set of values. Lower in the tree is higher priority.
 */
export function collectAllParams(
	routeSnap: ActivatedRouteSnapshot,
	key: 'params' | 'queryParams',
	currentParams: Params = {}
) {
	Object.assign(currentParams, routeSnap[key]);
	if (routeSnap.children.length) {
		routeSnap.children.forEach((childRoute) => collectAllParams(childRoute, key, currentParams));
	}
	return currentParams;
}

@Injectable()
export class RouteUtils {
	/**
	 * @deprecated Use allRouteParam$ instead, it's _likely_ what you want
	 */
	public snapshot = new BehaviorSubject<ActivatedRouteSnapshot>(null);
	public snapshotSync: ActivatedRouteSnapshot;
	private lastSnapshotSync: ActivatedRouteSnapshot;
	private _attemptedUrl: string;
	/**
	 * Provides an observable that emits the current set of route params and emits again whenever the route changes.
	 * This _likely_ obsoletes everything else on this class
	 */
	public allRouteParam$ = new BehaviorSubject<IAllParams>(makeAllParams());

	set userAttemptedUrl(url: string) {
		this._attemptedUrl = url;
	}

	get userAttemptedUrl() {
		return this._attemptedUrl;
	}

	public getMostChildish(snapshot: ActivatedRouteSnapshot) {
		let child = snapshot;
		while (child.firstChild !== null) {
			child = child.firstChild;
		}
		return child;
	}

	constructor(private router: Router) {
		this.router.events
			.pipe(
				filter((evt: Event): evt is ActivationEnd => evt instanceof ActivationEnd),
				switchMap((param: ActivationEnd) => {
					// console.log('sneeee', param?.snapshot?.toString(), this.router.routerState);
					const topLevelRouteSnapshot = param.snapshot.root.firstChild;
					if (topLevelRouteSnapshot.url.length && fullyAngularRoutes[topLevelRouteSnapshot.url[0].path]) {
						return EMPTY;
					}
					return of(param.snapshot);
				})
			)
			.subscribe(this.snapshot);

		this.listenCancelledNavigationToLogin();

		this.snapshot.subscribe((snapshot) => {
			this.lastSnapshotSync = this.snapshotSync;
			this.snapshotSync = snapshot;
		});

		this.allRouteParamGetter$().subscribe(this.allRouteParam$);
	}

	/** Mostly just for analytics related stuff */
	public routeName(snapshot?: ActivatedRouteSnapshot) {
		if (!snapshot) {
			snapshot = this.snapshotSync;
		}
		if (!snapshot) {
			return '';
		}

		while (snapshot.parent && snapshot.parent.url.length) {
			snapshot = snapshot.parent;
		}
		//...sure, don't even fully remember what this is doing. But it doesn't like angular child routes
		if (!snapshot.url.length) {
			return '';
		}
		return snapshot.url[0].path;
	}

	/**
	 * Provides an api for this extremely silly method of access the route params of a child route. Current iteration assumes
	 * there is only one router-outlet within the parent, and does not support nesting beyond one level.
	 * If multiple ever needs to be handled, can add a second arg for identifying the right one.
	 * @param parentRoute The [[ActivatedRoute]] taken from the injector of the parent component
	 * @deprecated Use allRouteParam$ instead, it's _likely_ what you want
	 */
	public childParamMap$(parentRoute: ActivatedRoute) {
		return this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			startWith(undefined as object), //rxjs is silly about undefined as a value
			switchMap((e) => parentRoute.firstChild?.paramMap)
		);
	}

	//for some reason angular doesn't just include this sort of thing...
	private allRouteParamGetter$() {
		return this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map(() => {
				const state = this.router.routerState.snapshot;
				const params = collectAllParams(state.root, 'params');
				const queryParams = collectAllParams(state.root, 'queryParams');

				return makeAllParams(params, queryParams);
			})
		);
	}

	/**
	 * returns the top level path when it changes
	 */
	public routeNameChange$() {
		return this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map((f) => {
				const state = this.router.routerState.snapshot;
				//first child of root node
				return this.routeName(state.root.firstChild);
			}),
			distinctUntilChanged()
		);
	}

	/**
	 * Listens for cancelled navigation events and stores the attempted URL.
	 * This is used to redirect the user to attempted URL after successful login.
	 * Note: This approach affects the logout flow,
	 * as the user will be redirected to the url they were when they logged out.
	 * This will happen just if the user logs out and logs in again without refreshing the page.
	 */
	private listenCancelledNavigationToLogin() {
		const start$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationStart),
			take(1)
		);
		const login$ = this.router.events.pipe(
			filter((event) => event instanceof ActivationStart && event?.snapshot?.component == LoginComponent),
			take(1)
		);
		combineLatest([start$, login$])
			.pipe(map(([navigationStartEvent, _]: [NavigationStart, ActivationStart]) => navigationStartEvent.url))
			.subscribe((attemptedUrl) => {
				this.userAttemptedUrl = attemptedUrl;
			});
	}
}
